/**
 * SWISS School Portal - Main App Component
 * ==========================================
 * Sets up routing and authentication context.
 */

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import CognitoCallback from './pages/CognitoCallback';
// Default landing for authenticated school users; kept eager so the
// first paint after sign-in doesn't flash a Suspense fallback.
import Dashboard from './pages/Dashboard';

// Everything below is code-split. Each lazy import becomes its own
// chunk that's only fetched when the user navigates to that route,
// dropping ~30 KB gz off the eager main.js bundle.

// SWISS Certification routes
const ProgressPage  = lazy(() => import('./pages/ProgressPage'));
const TeachersPage  = lazy(() => import('./pages/TeachersPage'));
const CouncilPage   = lazy(() => import('./pages/CouncilPage'));
const LabsPage      = lazy(() => import('./pages/LabsPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const SafeHandsResourcePage = lazy(() => import('./pages/SafeHandsResourcePage'));
const SafeHandsPage = lazy(() => import('./pages/SafeHandsPage'));
const WorkshopsPage = lazy(() => import('./pages/WorkshopsPage'));
const SettingsPage  = lazy(() => import('./pages/SettingsPage'));
const AnonReportsPage = lazy(() => import('./pages/AnonReportsPage'));
const CompliancePage  = lazy(() => import('./pages/CompliancePage'));
const StatutoryPage   = lazy(() => import('./pages/StatutoryPage'));
const SessionsPage    = lazy(() => import('./pages/SessionsPage'));
const IntellicarePage = lazy(() => import('./pages/IntellicarePage'));
const AuditPage       = lazy(() => import('./pages/AuditPage'));

// Crisis Management routes — a school admin without an active
// incident doesn't need any of this loaded on first paint.
const CrisisDashboard      = lazy(() => import('./pages/CrisisDashboard'));
const CrisisIncidents      = lazy(() => import('./pages/CrisisIncidents'));
const CrisisReport         = lazy(() => import('./pages/CrisisReport'));
const CrisisIncidentDetail = lazy(() => import('./pages/CrisisIncidentDetail'));
const CrisisProtocols      = lazy(() => import('./pages/CrisisProtocols'));

// Admin routes — heavy (~2,500 LOC combined), never visited by
// school users. Already split, kept here for parity.
const AdminDashboard    = lazy(() => import('./pages/AdminDashboard'));
const AdminSchoolDetail = lazy(() => import('./pages/AdminSchoolDetail'));
const AdminSystemDetail = lazy(() => import('./pages/AdminSystemDetail'));

// Public anonymous-reporting form — reached by students via a QR code,
// no auth. Its own chunk so it never weighs down the authenticated app.
const AnonReportPage    = lazy(() => import('./pages/AnonReportPage'));
const PublicScholarcareReferPage = lazy(() => import('./pages/PublicScholarcareReferPage'));
const PublicSchoolPage  = lazy(() => import('./pages/PublicSchoolPage'));
const MandatoryDisclosurePage = lazy(() => import('./pages/MandatoryDisclosurePage'));
const SessionSurveyPage = lazy(() => import('./pages/SessionSurveyPage'));
const PollPage          = lazy(() => import('./pages/PollPage'));
const CheckInPage       = lazy(() => import('./pages/CheckInPage'));
const AssessmentsPage   = lazy(() => import('./pages/AssessmentsPage'));
const SemhPage          = lazy(() => import('./pages/SemhPage'));
const SemhFullPage      = lazy(() => import('./pages/SemhPage').then((m) => ({ default: m.SemhFullPage })));
const SwissSswsAdmin    = lazy(() => import('./pages/SwissSswsAdmin'));
const SwissSswsCounsellor = lazy(() => import('./pages/SwissSswsCounsellor'));
const LdPage            = lazy(() => import('./pages/LdPage'));
const LdFullPage        = lazy(() => import('./pages/LdPage').then((m) => ({ default: m.LdFullPage })));
const SwissScreenAdmin      = lazy(() => import('./pages/SwissScreenAdmin'));
const SwissScreenCounsellor = lazy(() => import('./pages/SwissScreenCounsellor'));
const ParentReviewPage      = lazy(() => import('./pages/ParentReviewPage'));
const ParentConsentPage     = lazy(() => import('./pages/ParentConsentPage'));

// SWISS WellTrack (ScholarCare) — licensed counselor caseload module.
const WellTrackPage          = lazy(() => import('./pages/WellTrackPage'));
const WellTrackStudentsPage  = lazy(() => import('./pages/WellTrackStudentsPage'));
const WellTrackStudentProfilePage = lazy(() => import('./pages/WellTrackStudentProfilePage'));
const WellTrackIntakePage    = lazy(() => import('./pages/WellTrackIntakePage'));
const WellTrackSessionsPage  = lazy(() => import('./pages/WellTrackSessionsPage'));
const WellTrackReferralsPage = lazy(() => import('./pages/WellTrackReferralsPage'));
const WellTrackReportsPage   = lazy(() => import('./pages/WellTrackReportsPage'));
const WellTrackCrisisPage    = lazy(() => import('./pages/WellTrackCrisisPage'));

// Single shared QueryClient. Stale-time defaults keep dashboards
// fresh without polling intervals or persistent connections —
// matches the Elevate cost profile (no AppSync, no WebSockets).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,      // 1 min — data refetches on next mount/focus after this
      retry: 1,
      refetchOnWindowFocus: true, // dashboards re-validate when user returns to tab
    },
  },
});

// Shown to a group admin whose getMe() never resolved (server error,
// stale session, deleted group row, etc.). Gives them an explicit way
// out instead of an infinite spinner.
const NetworkLoadFailure = () => {
  const { logout } = useAuth();
  const handleSignOut = () => {
    logout();
    window.location.href = '/login';
  };
  const handleRetry = () => window.location.reload();
  return (
    <div className="loading-screen">
      <h2 style={{ margin: 0, fontSize: 18 }}>Could not load your group</h2>
      <p style={{ maxWidth: 420, textAlign: 'center' }}>
        We could not fetch your group right now. Your session may have
        expired, or the group you manage may have been removed. Try
        again, or sign in fresh.
      </p>
      <div style={{ display: 'flex', gap: 12 }}>
        <button type="button" className="btn btn-outline" onClick={handleRetry}>
          Try again
        </button>
        <button type="button" className="btn btn-swiss" onClick={handleSignOut}>
          Sign out
        </button>
      </div>
    </div>
  );
};

// Protected Route wrapper — for school dashboards (school auth required)
const ProtectedRoute = ({ children }) => {
  const {
    isAuthenticated, loading, isAdmin, isSystemAdmin, isAuditor, systemId, viewAsSchool,
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 3rd-party auditors are confined to the Physical Audit module.
  if (isAuditor) {
    const p = location.pathname;
    if (p !== '/audit' && p !== '/settings') {
      return <Navigate to="/audit" replace />;
    }
    return children;
  }

  // Admins without an active school preview go back to /admin.
  // When an admin is impersonating a school, let them see the
  // school-side dashboard for QA / demos.
  if (isAdmin && !viewAsSchool) {
    return <Navigate to="/admin" replace />;
  }

  // Network admins should never see the school portal — their dashboard
  // is the network page. Catches manual navigation to / or /dashboard,
  // bookmarks, and any other route guard fallthroughs.
  if (isSystemAdmin && !viewAsSchool) {
    if (systemId) {
      return <Navigate to={`/admin/systems/${systemId}`} replace />;
    }
    // We're past the loading gate but still missing systemId — getMe
    // failed during session-restore. Show a recoverable error UI
    // instead of an infinite spinner.
    return <NetworkLoadFailure />;
  }

  return children;
};

// Admin Protected Route — MSS admins, super admins, and system admins
// (the latter scoped to their own system).
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, isSystemAdmin, systemId } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // System admins can only see their own system's detail page (and
  // anything that resolves to a school in their system, which the
  // backend gates). Anything else on /admin bounces them to their
  // own system page.
  if (isSystemAdmin && !isAdmin) {
    const allowedPrefix = systemId ? `/admin/systems/${systemId}` : null;
    const path = location.pathname;
    if (!allowedPrefix || (!path.startsWith(allowedPrefix) && !path.startsWith('/admin/schools/'))) {
      return systemId ? <Navigate to={allowedPrefix} replace /> : <Navigate to="/dashboard" replace />;
    }
    return children;
  }

  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

// Public Route wrapper (redirects to the appropriate dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, isSystemAdmin, isAuditor, systemId } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    let dest = '/';
    if (isAdmin) dest = '/admin';
    else if (isSystemAdmin && systemId) dest = `/admin/systems/${systemId}`;
    else if (isAuditor) dest = '/audit';
    return <Navigate to={dest} replace />;
  }

  return children;
};

const RouteFallback = () => (
  <div className="loading-screen">
    <div className="loading-spinner" />
    <p>Loading…</p>
  </div>
);

// Bridges the push service worker to React Query. When sw.js receives
// a Web Push for a new anonymous report it postMessages every open
// tab; we catch that here and invalidate the anonymous-report queries
// so any visible dashboard (Overview card, overdue banner) re-fetches
// itself — live updates with no polling, no WebSocket.
function usePushQueryBridge() {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === 'anon-report-received') {
        queryClient.invalidateQueries({ queryKey: ['anon'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [queryClient]);
}

function AppRoutes() {
  usePushQueryBridge();
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Cognito Hosted UI redirects back here with ?code=... */}
      <Route path="/auth/callback" element={<CognitoCallback />} />

      {/* Public school wellbeing dashboard — no auth, linked from the
          school's website or brochure. Slug matches school.publicSlug
          or slugified shortName. 404s for slugs whose school hasn't
          opted in, same as unknown slugs (no enumeration leak). */}
      <Route path="/school/:slug" element={<PublicSchoolPage />} />
      {/* Same page, opened at the CBSE mandatory disclosure — the link
          a school puts behind its website's "Mandatory Public
          Disclosure" menu. */}
      <Route path="/school/:slug/disclosure" element={<PublicSchoolPage />} />

      {/* Public anonymous-reporting form — no auth, reached via QR code.
          Deliberately outside every route guard: a student submitting a
          report has no account and must never be bounced to /login. */}
      <Route path="/report/:token" element={<AnonReportPage />} />

      {/* Public SWISS WellTrack teacher-referral form — no auth,
          reached via the link/QR code a school shares with its
          teachers. Deliberately outside every route guard, same
          reasoning as /report/:token — a teacher with no SWISS
          account must never be bounced to /login. */}
      <Route path="/scholarcare/refer/:token" element={<PublicScholarcareReferPage />} />

      {/* Public parent review of a SWISS Screen result — no auth,
          reached via the secure link the counsellor sends from
          /ld/screen/counsellor. Token validation lives inside the
          page (server-side in production). Lives on the same
          subdomain as the portal so no extra DNS is needed. */}
      <Route path="/parent/screen/:studentId" element={<ParentReviewPage />} />

      {/* Public parent consent form — no auth, reached via the
          consent link the counsellor sends from the Bulk consent
          flow or Edit student modal. Records the parent's decision
          and updates the student's record. */}
      <Route path="/parent/consent/:studentId" element={<ParentConsentPage />} />

      {/* Public session attendance + feedback survey — no auth, reached
          via the QR code a presenter places on their slide. Also outside
          every route guard. */}
      <Route path="/session/:token" element={<SessionSurveyPage />} />

      {/* Public per-session poll (pre/post survey) — no auth, reached
          via its own QR / WhatsApp link. */}
      <Route path="/poll/:token" element={<PollPage />} />

      {/* Public Student Wellbeing Check-In — no auth, reached via the
          QR / link a teacher shares with a class. */}
      <Route path="/checkin/:token" element={<CheckInPage />} />

      {/* Admin Routes — MSS admins / superadmins */}
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/schools/:id"
        element={
          <AdminProtectedRoute>
            <AdminSchoolDetail />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/admin/systems/:id"
        element={
          <AdminProtectedRoute>
            <AdminSystemDetail />
          </AdminProtectedRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* SWISS Certification Routes */}
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="progress" element={<ProgressPage />} />
        <Route path="teachers" element={<TeachersPage />} />
        <Route path="council" element={<CouncilPage />} />
        <Route path="anon-reports" element={<AnonReportsPage />} />
        <Route path="intellicare" element={<IntellicarePage />} />
        <Route path="compliance" element={<CompliancePage />} />
        <Route path="posh" element={<StatutoryPage kind="posh" />} />
        <Route path="pocso" element={<StatutoryPage kind="pocso" />} />
        <Route path="labs" element={<LabsPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="resources/safe-hands" element={<SafeHandsResourcePage />} />
        {/* SWISS Safe Hands — staff onboarding, licensed add-on `safehands` */}
        <Route path="safe-hands" element={<SafeHandsPage />} />
        <Route path="workshops" element={<WorkshopsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="disclosure" element={<MandatoryDisclosurePage />} />
        <Route path="assessments" element={<AssessmentsPage />} />
        {/* SSWS (Student Wellbeing Screen) — same shim pattern as
            /ld. /semh routes by role; /semh/full keeps the original
            live module dashboard + Assessments tooling. */}
        <Route path="semh" element={<SemhPage />} />
        <Route path="semh/full" element={<SemhFullPage />} />
        <Route path="semh/screen" element={<SwissSswsAdmin />} />
        <Route path="semh/screen/counsellor" element={<SwissSswsCounsellor />} />
        {/* /ld routes to the SwissScreen view that matches the user's
            role (counsellor → counsellor view; admin / council / MSS
            admin → aggregate view). /ld/full keeps the original live
            module dashboard + embedded Assessments tooling. */}
        <Route path="ld" element={<LdPage />} />
        <Route path="ld/full" element={<LdFullPage />} />
        <Route path="ld/screen" element={<SwissScreenAdmin />} />
        <Route path="ld/screen/counsellor" element={<SwissScreenCounsellor />} />

        {/* SWISS WellTrack (ScholarCare) — role-routed entry at
            /scholarcare; identifiable sub-routes are counsellor-only,
            enforced again server-side. Deliberately disjoint from
            /crisis/* (the unrelated physical-safety Crisis module). */}
        <Route path="scholarcare" element={<WellTrackPage />} />
        <Route path="scholarcare/students" element={<WellTrackStudentsPage />} />
        <Route path="scholarcare/students/:studentId" element={<WellTrackStudentProfilePage />} />
        <Route path="scholarcare/intake" element={<WellTrackIntakePage />} />
        <Route path="scholarcare/intake/:studentId/assess" element={<WellTrackIntakePage />} />
        <Route path="scholarcare/sessions" element={<WellTrackSessionsPage />} />
        <Route path="scholarcare/sessions/new" element={<WellTrackSessionsPage />} />
        <Route path="scholarcare/sessions/new/:studentId" element={<WellTrackSessionsPage />} />
        <Route path="scholarcare/referrals" element={<WellTrackReferralsPage />} />
        <Route path="scholarcare/reports" element={<WellTrackReportsPage />} />
        <Route path="scholarcare/crisis" element={<WellTrackCrisisPage />} />

        <Route path="settings" element={<SettingsPage />} />

        {/* Crisis Management Routes */}
        <Route path="crisis" element={<CrisisDashboard />} />
        <Route path="crisis/dashboard" element={<CrisisDashboard />} />
        <Route path="crisis/report" element={<CrisisReport />} />
        <Route path="crisis/incidents" element={<CrisisIncidents />} />
        <Route path="crisis/incidents/:id" element={<CrisisIncidentDetail />} />
        <Route path="crisis/protocols" element={<CrisisProtocols />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
